import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../../config';

const AntiFraudPage = ({ onShowToast }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [configLoading, setConfigLoading] = useState(true);
    const [antifraudEnabled, setAntifraudEnabled] = useState(true);
    const [windowValue, setWindowValue] = useState('1');
    const [windowUnit, setWindowUnit] = useState('month');
    const [maxExpensiveOrders, setMaxExpensiveOrders] = useState('3');
    const [expensiveOrderThresholdRub, setExpensiveOrderThresholdRub] = useState('500');
    const [username, setUsername] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);

    const hoursToWindow = (hours) => {
        const value = Number(hours || 720);
        if (value % 720 === 0) return { value: String(value / 720), unit: 'month' };
        if (value % 168 === 0) return { value: String(value / 168), unit: 'week' };
        return { value: String(value), unit: 'hour' };
    };

    const windowToHours = () => {
        const value = Math.max(1, Number(windowValue || 1));
        if (windowUnit === 'month') return Math.round(value * 720);
        if (windowUnit === 'week') return Math.round(value * 168);
        return Math.round(value);
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setConfigLoading(true);
            const [configRes, res] = await Promise.all([
                fetch(`${API_BASE_URL}/api/anti-fraud/config`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }),
                fetch(`${API_BASE_URL}/api/anti-fraud/banned-recipients`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }),
            ]);
            if (configRes.ok) {
                const config = await configRes.json();
                setAntifraudEnabled(Boolean(config.enabled));
                const parsed = hoursToWindow(config.limit_window_hours);
                setWindowValue(parsed.value);
                setWindowUnit(parsed.unit);
                setMaxExpensiveOrders(String(config.max_expensive_orders || 3));
                setExpensiveOrderThresholdRub(String(config.expensive_order_threshold_rub || 500));
            }
            if (!res.ok) throw new Error('Failed to load anti-fraud list');
            setItems(await res.json());
        } finally {
            setLoading(false);
            setConfigLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saveRecipient = async (e) => {
        e.preventDefault();
        const cleanUsername = username.trim().replace(/^@+/, '');
        if (!cleanUsername) {
            onShowToast?.('Укажите username получателя');
            return;
        }
        try {
            setSaving(true);
            const res = await fetch(`${API_BASE_URL}/api/anti-fraud/banned-recipients`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: cleanUsername,
                    reason: reason.trim() || null,
                    is_active: true,
                }),
            });
            if (!res.ok) throw new Error('save failed');
            setUsername('');
            setReason('');
            onShowToast?.('Получатель Stars забанен');
            await load();
        } catch (_) {
            onShowToast?.('Не удалось сохранить бан получателя');
        } finally {
            setSaving(false);
        }
    };

    const saveConfig = async () => {
        try {
            setSavingConfig(true);
            const res = await fetch(`${API_BASE_URL}/api/anti-fraud/config`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    enabled: antifraudEnabled,
                    limit_window_hours: windowToHours(),
                    max_expensive_orders: Math.max(1, Number(maxExpensiveOrders || 3)),
                    expensive_order_threshold_rub: Math.max(1, Number(expensiveOrderThresholdRub || 500)),
                }),
            });
            if (!res.ok) throw new Error('config save failed');
            const config = await res.json();
            setAntifraudEnabled(Boolean(config.enabled));
            const parsed = hoursToWindow(config.limit_window_hours);
            setWindowValue(parsed.value);
            setWindowUnit(parsed.unit);
            setMaxExpensiveOrders(String(config.max_expensive_orders || 3));
            setExpensiveOrderThresholdRub(String(config.expensive_order_threshold_rub || 500));
            onShowToast?.('Настройки антифрода сохранены');
        } catch (_) {
            onShowToast?.('Не удалось сохранить настройки антифрода');
        } finally {
            setSavingConfig(false);
        }
    };

    const setActive = async (item, isActive) => {
        const res = await fetch(`${API_BASE_URL}/api/anti-fraud/banned-recipients/${item.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_active: isActive }),
        });
        if (!res.ok) {
            onShowToast?.('Не удалось изменить статус');
            return;
        }
        setItems((current) =>
            current.map((row) => (row.id === item.id ? { ...row, is_active: isActive } : row)),
        );
    };

    const deleteItem = async (item) => {
        const res = await fetch(`${API_BASE_URL}/api/anti-fraud/banned-recipients/${item.id}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
            onShowToast?.('Не удалось удалить запись');
            return;
        }
        setItems((current) => current.filter((row) => row.id !== item.id));
    };

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-bold text-slate-800">Антифрод</h2>
                <p className="text-sm text-slate-500 mt-1">
                    Бан получателей Stars по username и контроль антифрод-ограничений.
                </p>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-slate-800">Настройки антифрод-системы</h3>
                {configLoading ? (
                    <div className="py-4 text-slate-500">Загрузка настроек...</div>
                ) : (
                    <>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={antifraudEnabled}
                                onChange={(e) => setAntifraudEnabled(e.target.checked)}
                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm text-slate-700">
                                Включить антифрод проверки в боте
                            </span>
                        </label>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Период, за который считаются дорогие успешные отправки
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={windowValue}
                                    onChange={(e) => setWindowValue(e.target.value)}
                                    className="p-2 border rounded-lg text-sm"
                                />
                                <select
                                    value={windowUnit}
                                    onChange={(e) => setWindowUnit(e.target.value)}
                                    className="p-2 border rounded-lg text-sm"
                                >
                                    <option value="hour">Часы</option>
                                    <option value="week">Недели</option>
                                    <option value="month">Месяцы</option>
                                </select>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                В базу сохраняется {windowToHours()} ч. Месяц считается как 30 дней.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Максимум дорогих успешных отправок
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={maxExpensiveOrders}
                                    onChange={(e) => setMaxExpensiveOrders(e.target.value)}
                                    className="w-full p-2 border rounded-lg text-sm"
                                    placeholder="3"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Дорогой заказ от суммы, ₽
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    step="0.01"
                                    value={expensiveOrderThresholdRub}
                                    onChange={(e) => setExpensiveOrderThresholdRub(e.target.value)}
                                    className="w-full p-2 border rounded-lg text-sm"
                                    placeholder="500"
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            disabled={savingConfig}
                            onClick={saveConfig}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm disabled:opacity-60"
                        >
                            {savingConfig ? 'Сохранение...' : 'Сохранить настройки'}
                        </button>
                    </>
                )}
            </div>

            <form onSubmit={saveRecipient} className="bg-white border border-slate-100 rounded-xl p-4 space-y-3">
                <h3 className="font-semibold text-slate-800">Забанить получателя Stars</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="username без @ или с @"
                        className="p-2 border rounded-lg text-sm"
                    />
                    <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Причина, необязательно"
                        className="p-2 border rounded-lg text-sm"
                    />
                </div>
                <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm disabled:opacity-60"
                >
                    Забанить получателя
                </button>
            </form>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold text-slate-800 mb-3">Забаненные получатели</h3>
                {loading ? (
                    <div className="py-6 text-slate-500 text-center">Загрузка...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-500 border-b">
                                    <th className="py-2 pr-3">Username</th>
                                    <th className="py-2 pr-3">Причина</th>
                                    <th className="py-2 pr-3">Статус</th>
                                    <th className="py-2 pr-3">Дата</th>
                                    <th className="py-2 pr-3">Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id} className="border-b last:border-b-0">
                                        <td className="py-2 pr-3">@{item.username}</td>
                                        <td className="py-2 pr-3">{item.reason || '—'}</td>
                                        <td className={`py-2 pr-3 ${item.is_active ? 'text-rose-600' : 'text-slate-500'}`}>
                                            {item.is_active ? 'Активен' : 'Отключен'}
                                        </td>
                                        <td className="py-2 pr-3">
                                            {item.created_at ? new Date(item.created_at).toLocaleString('ru-RU') : '—'}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    onClick={() => setActive(item, !item.is_active)}
                                                    className="px-3 py-1.5 border rounded-lg text-xs"
                                                >
                                                    {item.is_active ? 'Отключить' : 'Включить'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteItem(item)}
                                                    className="px-3 py-1.5 bg-slate-700 text-white rounded-lg text-xs"
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!items.length && <div className="py-6 text-slate-500 text-center">Список пуст</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AntiFraudPage;
